import { Box, Typography } from '@mui/material';
import { Column, useFilters, useSortBy, useTable } from 'react-table';
import { TierCategory } from 'shared/types';
import { DateColumn } from './columns/date-column';
import { EditableColumn } from './columns/editable-column';
import { ParameterTable } from './parameter-table';

interface TierCategorySectionProps {
    categoryName: string;
    data: TierCategory[];
}
export function TierCategorySection({ data, categoryName }: TierCategorySectionProps) {
    const instance = useTierCategoryTableInstance(data);
    return (
        <Box>
            <ParameterTable title={categoryName} instance={instance} />
        </Box>
    );
}

const getRowId = (row: TierCategory) => row.uniqueId;

export const defaultColumn: Partial<Column<TierCategory>> = {
    Filter: () => null,
    Cell: ({ value = '' }) => <Typography variant='body2'>{value}</Typography>,
    disableFilters: true,
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    align: 'inherit',
    percent: true,
};

const marginTargetColumns: Column<TierCategory>[] = [
    {
        Header: 'Product Tier',
        accessor: 'productTier',
        align: 'left',
    },
    {
        Header: 'Tier Threshold',
        accessor: 'tierThreshold',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Tier Threshold cannot be less than 0', value: 0 } },
        title: '% of revenue cutoff for a product to be included in the product tier',
    },
    {
        Header: 'Increase Cap',
        accessor: 'increaseCap',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Increase Cap cannot be less than 0', value: 0 } },
        title: 'max % that product price can increase, compared to the price at the end of 2021',
    },
    {
        Header: 'Color Variant',
        accessor: 'colorVariantAdder',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Color Variant cannot be less than 0', value: 0 } },
        title: '% to increase price for all color variants of a product',
    },
    {
        Header: 'Competitive',
        accessor: 'competitiveAdder',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Competition cannot be less than 0', value: 0 } },
        title: '% to increase price for products using a competitive match',
    },
    {
        Header: 'Margin Floor',
        accessor: 'marginFloor',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Margin Floor cannot be less than 0', value: 0 } },
        title: 'lowest acceptable product margin %',
    },
    {
        Header: 'Inflation',
        accessor: 'inflationAdder',
        align: 'left',
        Cell: EditableColumn,
        section: 'tierCategories',
        rules: { min: { message: 'Inflation cannot be less than 0', value: 0 } },
        title: '% to increase price for all products, due to inflation',
    },
    {
        Header: 'Last Updated',
        accessor: 'lastUpdated',
        align: 'left',
        Cell: DateColumn,
        section: 'tierCategories',
    },
];

export function useTierCategoryTableInstance(data: TierCategory[]) {
    return useTable<TierCategory>(
        {
            columns: marginTargetColumns,
            data,
            defaultColumn,
            getRowId,
            autoResetFilters: false,
            autoResetSortBy: false,
        },
        useFilters,
        useSortBy
    );
}
